import { STATUS, WEEKDAYS_SHORT, addDays, eventSlot, fmtDate, fmtDateLong, parseISO, playerLabel, startOfWeek, toISO, toMin, todayBerlin, weekdayIdx, euro } from "./calendarUtils";
import { BlockChip } from "./TimeBlocks";

const HOUR_START = 8;
const HOUR_END = 22;
const PX_PER_HOUR = 56;

const EventChip = ({ e, onOpen, compact }) => {
  const s = STATUS[e.display_status] || STATUS.pending;
  const slot = eventSlot(e);
  return (
    <button
      type="button"
      onClick={() => onOpen(e)}
      className={`w-full text-left border-l-4 border rounded-sm px-2 py-1 text-xs transition-transform duration-150 hover:scale-[1.02] hover:z-20 ${s.chip}`}
      data-testid={`cal-event-${e.appointment_id}`}
      title={`${slot.time}–${slot.end_time} ${e.customer?.name} · ${e.training_name} · ${s.label}`}
    >
      <div className="font-semibold truncate">{slot.time} {e.customer?.name}</div>
      {!compact && <div className="truncate opacity-80">{e.training_name}{playerLabel(e) ? ` · ${playerLabel(e)}` : ""}</div>}
    </button>
  );
};

const DayBackground = ({ dayInfo }) =>
  dayInfo?.length ? (
    <div className="absolute inset-x-0 top-0 z-0 px-1 pt-1 space-y-1 pointer-events-none">
      {dayInfo.map((p) => (
        <div key={p.id || p.name} className="text-[10px] uppercase tracking-wider text-orange-300/80 bg-orange-500/10 px-1 truncate">{p.name}</div>
      ))}
    </div>
  ) : null;

export function TimeGrid({ days, events, onOpen, periodsFor, blocks = [], onBlock, holidays = {} }) {
  const today = todayBerlin();
  const hours = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);
  return (
    <div className="overflow-x-auto" data-testid="cal-timegrid">
      <div className="min-w-[640px]">
        <div className="grid" style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(0,1fr))` }}>
          <div />
          {days.map((d) => (
            <div key={d} className={`px-2 py-2 text-center border-b border-white/10 ${d === today ? "text-[#c1ff72]" : "text-white"}`}>
              <div className="text-xs text-[#71717A]">{WEEKDAYS_SHORT[weekdayIdx(d)]}</div>
              <div className="font-['Bebas_Neue'] text-xl">{fmtDate(d).slice(0, 5)}</div>
              {holidays[d] && <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide bg-sky-400/15 text-sky-200 px-1 truncate" data-testid={`cal-holiday-${d}`}>Feiertag: {holidays[d]}</div>}
            </div>
          ))}
          <div className="relative" style={{ height: hours.length * PX_PER_HOUR }}>
            {hours.map((h) => (
              <div key={h} className="absolute right-2 text-[11px] text-[#71717A]" style={{ top: (h - HOUR_START) * PX_PER_HOUR - 6 }}>{String(h).padStart(2, "0")}:00</div>
            ))}
          </div>
          {days.map((d) => (
            <div key={d} className={`relative border-l border-white/5 ${d === today ? "bg-[#c1ff72]/[0.03]" : ""}`} style={{ height: hours.length * PX_PER_HOUR }} data-testid={`cal-day-col-${d}`}>
              {hours.map((h) => <div key={h} className="absolute inset-x-0 border-t border-white/5" style={{ top: (h - HOUR_START) * PX_PER_HOUR }} />)}
              <DayBackground dayInfo={periodsFor(d)} />
              {blocks.filter((b) => b.date === d).map((b) => (
                <BlockChip key={b.id + b.date} b={b} onOpen={onBlock} top={((toMin(b.start) - HOUR_START * 60) / 60) * PX_PER_HOUR} height={Math.max(((toMin(b.end) - toMin(b.start)) / 60) * PX_PER_HOUR - 2, 18)} />))}
              {events.filter((e) => eventSlot(e).date === d).map((e) => {
                const slot = eventSlot(e);
                const top = ((toMin(slot.time) - HOUR_START * 60) / 60) * PX_PER_HOUR;
                const height = Math.max(((toMin(slot.end_time) - toMin(slot.time)) / 60) * PX_PER_HOUR - 2, 22);
                return (
                  <div key={e.appointment_id} className="absolute inset-x-1 z-10" style={{ top, height }}>
                    <div className="h-full [&>button]:h-full"><EventChip e={e} onOpen={onOpen} compact={height < 40} /></div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function MonthView({ anchor, events, onOpen, onPickDay, periodsFor, holidays = {} }) {
  const first = toISO(new Date(parseISO(anchor).getFullYear(), parseISO(anchor).getMonth(), 1));
  const start = startOfWeek(first);
  const month = parseISO(anchor).getMonth();
  const today = todayBerlin();
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  return (
    <div data-testid="cal-month">
      <div className="grid grid-cols-7 text-center text-xs text-[#71717A] mb-1">{WEEKDAYS_SHORT.map((d) => <div key={d} className="py-1">{d}</div>)}</div>
      <div className="grid grid-cols-7 gap-px bg-white/5">
        {cells.map((d) => {
          const dayEvents = events.filter((e) => eventSlot(e).date === d);
          const inMonth = parseISO(d).getMonth() === month;
          return (
            <div key={d} className={`bg-[#121212] min-h-[72px] sm:min-h-[108px] p-1 ${inMonth ? "" : "opacity-40"}`} data-testid={`cal-month-cell-${d}`}>
              <button type="button" onClick={() => onPickDay(d)} className={`text-xs font-semibold mb-1 px-1 rounded-sm hover:bg-white/10 ${d === today ? "bg-[#c1ff72] text-black" : "text-white"}`}>{parseISO(d).getDate()}</button>
              {holidays[d] && <div className="text-[9px] uppercase text-sky-300 truncate" data-testid={`cal-month-holiday-${d}`}>{holidays[d]}</div>}
              {periodsFor(d).length > 0 && <div className="hidden sm:block text-[9px] uppercase text-orange-300/80 truncate">{periodsFor(d)[0].name}</div>}
              <div className="hidden sm:block space-y-1">
                {dayEvents.slice(0, 3).map((e) => <EventChip key={e.appointment_id} e={e} onOpen={onOpen} compact />)}
                {dayEvents.length > 3 && <button type="button" onClick={() => onPickDay(d)} className="text-[10px] text-[#A1A1AA] hover:text-white">+{dayEvents.length - 3} weitere</button>}
              </div>
              <div className="sm:hidden flex flex-wrap gap-1">
                {dayEvents.map((e) => <span key={e.appointment_id} className={`w-2 h-2 rounded-full ${(STATUS[e.display_status] || STATUS.pending).dot}`} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ListView({ events, onOpen, empty, testId }) {
  if (!events.length) return <p className="text-center text-[#71717A] py-12" data-testid={`${testId}-empty`}>{empty}</p>;
  let lastDate = null;
  return (
    <div className="space-y-2" data-testid={testId}>
      {events.map((e) => {
        const slot = eventSlot(e);
        const s = STATUS[e.display_status] || STATUS.pending;
        const header = slot.date !== lastDate ? (lastDate = slot.date) : null;
        return (
          <div key={e.appointment_id}>
            {header && <h4 className="text-xs uppercase tracking-widest text-[#71717A] mt-4 mb-2">{fmtDateLong(header)}</h4>}
            <button type="button" onClick={() => onOpen(e)} className="w-full text-left bg-[#1A1A1A] border border-white/5 hover:border-[#c1ff72]/40 transition-colors p-3 flex flex-wrap items-center gap-3" data-testid={`list-item-${e.appointment_id}`}>
              <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
              <span className="font-['Bebas_Neue'] text-xl text-white w-28">{slot.time}–{slot.end_time}</span>
              <span className="flex-1 min-w-[160px]">
                <span className="block text-white font-medium">{e.customer?.name}{playerLabel(e) ? <span className="text-[#A1A1AA] font-normal"> · Spieler: {playerLabel(e)}</span> : null}</span>
                <span className="block text-xs text-[#A1A1AA]">{e.training_name} · {e.duration} Min{e.player_count > 1 ? ` · ${e.player_count} Teilnehmer` : ""}</span>
              </span>
              <span className={`text-xs font-semibold ${s.text}`}>{s.label}</span>
              <span className="text-sm text-[#c1ff72] w-24 text-right">{e.payment_method === "training_card" ? "Karte" : euro(e.total_price)}</span>
            </button>
          </div>
        );
      })}
    </div>
  );
}
